import { VFC, useState, useContext } from "react";
// Libraries
import * as Yup from "yup";
import { Formik } from "formik";
import { useHoldingInfo } from "@breedr/utils";
// Utils
import { GetAnimalsList_animalsExtended_animals } from "api/MyLivestock/Animal/types/GetAnimalsList";
import { GetContactList_contacts } from "api/AddressBook/types/GetContactList";
import { CommonContext } from "config/commonProvider";
import { TableOptionsContext } from "config/tableOptionsProvider";
import { MOVE_OFF_FARM_TEMPLATE } from "constants/Animals";
import { ELEMENTS_IDS } from "constants/ElementsIds";
import {
  useHasFeature,
  useBulkMarkAnimalsOffFarmMutation,
  useGetCurrentBusinessUnit,
  useGetActivityTemplates,
} from "hooks";
import { handleErrorCheck } from "helpers/general";
// Components
import { Button, FieldDate, FieldText, Modal, Spacer, Text, Flex, Title } from "components";
import BulkMoveOffFarm from "./BulkMoveOffFarm";
import { BUWithLocationSelect } from "components/MyLivestock";
import { AddressBookSelect } from "components/Settings/AddressBook/AddressBookSelect";
import { formatAddressString } from "components/Settings/AddressBook/AddressListTable";
import { NewContactModalContent } from "components/Settings/AddressBook/NewContactModalContent";

export const TEST_IDS = {
  MARK_OFF_FARM_BUTTON_TEST_ID: "mark-off-farm-button",
  MODAL_CANCEL_BUTTON_TEST_ID: "mark-off-farm-cancel-btn",
  MODAL_CONFIRM_BUTTON_TEST_ID: "mark-off-farm-confirm-btn",
};

interface ConfirmMarkOffFarmModalProps {
  animals: GetAnimalsList_animalsExtended_animals[];
  groupId?: string;
  isShowModal?: boolean;
  isSingleAnimal?: boolean;
  onClose?: () => void;
  tableId: string;
}

export type MarkOffFarmValues = {
  contact?: string;
  deliveryDate?: Date;
  destinationCPHNumber?: string;
  destinationName?: string;
  destinationId?: string;
  destinationPostcode?: string;
  destinationAddress?: string;
  destinationEmail?: string;
  notes?: string;
  location?: string;
};

export const initialValues = {
  contact: "",
  deliveryDate: new Date(),
  destinationCPHNumber: "",
  destinationName: "",
  destinationId: "",
  destinationPostcode: "",
  destinationAddress: "",
  destinationEmail: "",
  notes: "",
  location: "",
};

export const moveOffFarmSchema = Yup.object().shape({
  destinationEmail: Yup.string().email("please enter a valid email address"),
});

export const ConfirmMarkOffFarmModal: VFC<ConfirmMarkOffFarmModalProps> = ({
  animals = [],
  groupId,
  isShowModal = false,
  isSingleAnimal = false,
  onClose,
  tableId,
}) => {
  const [showModal, setShowModal] = useState(isShowModal);
  const [showAddContactForm, setShowAddContactForm] = useState(false);
  const { showNotification } = useContext(CommonContext);
  const { setTableOptions } = useContext(TableOptionsContext);
  const isShowMoveToBUSelector = useHasFeature("SHOW_MOVE_TO_BU_SELECTOR");

  const { data: activityTemplates } = useGetActivityTemplates();

  const activityTemplate = activityTemplates?.find((el) => el.slug === MOVE_OFF_FARM_TEMPLATE);

  const animalsCanBeRemove = animals.filter((a) => !a.rflId);
  const animalsCouldNotRemove = animals.filter((a) => a.rflId);
  const isAllAnimalsCanNotBeRemoved = animalsCanBeRemove.length === 0;
  const isDisableAllExceptForm = showAddContactForm || isAllAnimalsCanNotBeRemoved;

  const { regulator, country } = useGetCurrentBusinessUnit();
  //@ts-expect-error: ts-mismatch
  const { HoldingIDInitialism } = useHoldingInfo(country?.isoAlpha2);
  const isScotMoves = regulator?.slug === "SCOTMOVES";

  const { mutate: markAnimalsOffFarm, loading: requesting } = useBulkMarkAnimalsOffFarmMutation({
    animals: animalsCanBeRemove,
    activityTemplate: activityTemplate ? activityTemplate : null,
    groupId,
  });

  const handleModalToggle = (): void => {
    setShowModal(!showModal);
    onClose && onClose();
  };

  const handleCancelClick = (e): void => {
    e.stopPropagation();
    handleModalToggle();
  };

  const handleConfirmClick = async (values): Promise<void> => {
    try {
      await markAnimalsOffFarm(values);
      setTableOptions({
        id: tableId,
        selectedRows: [],
        selectedRowsIds: null,
      });
      handleModalToggle();
      showNotification({
        message: "Animals were successfully moved off-farm",
      });
      setShowAddContactForm(false);
    } catch (e) {
      showNotification({
        variant: "error",
        message: "Error moving animals",
      });
    }
  };

  const handleFormSubmit = (values: MarkOffFarmValues): void => {
    handleConfirmClick(values);
  };

  return (
    <>
      {!isSingleAnimal && (
        <Button
          id={ELEMENTS_IDS.BULK_MARK_OFF_FARM_BUTTON}
          caption="Mark off-farm"
          onClick={handleModalToggle}
          data-testid={TEST_IDS.MARK_OFF_FARM_BUTTON_TEST_ID}
        />
      )}
      <Modal
        buttonsLeftSide
        active={showModal}
        handleClose={handleModalToggle}
        title="Mark animal(s) off-farm?"
        subTitle=""
      >
        <>
          {!isSingleAnimal && (
            <BulkMoveOffFarm
              allAnimalsCount={animals.length}
              animalsCanBeRemove={animalsCanBeRemove}
              animalsCouldNotRemove={animalsCouldNotRemove}
            />
          )}
          <Formik
            enableReinitialize
            onSubmit={handleConfirmClick}
            initialValues={initialValues}
            validateOnChange={false}
            validationSchema={moveOffFarmSchema}
          >
            {({ values, errors, handleChange, setFieldValue, handleSubmit, setValues }): JSX.Element => {
              const handleLocationChange = (newValues: MarkOffFarmValues): void => {
                const newOldValues = {
                  ...values,
                  ...newValues,
                  contact: "",
                };
                setValues(newOldValues);
              };

              const handleContactChange = (contact: GetContactList_contacts | undefined): void => {
                const newValues = {
                  contact: contact?.id || "",
                  location: "",
                  destinationName: contact?.name || "",
                  destinationCPHNumber: contact?.farmId || "",
                  destinationPostcode: contact?.postcode || "",
                  destinationEmail: contact?.email || "",
                  destinationAddress: contact ? formatAddressString(contact) : "",
                };
                const newOldValues = {
                  ...values,
                  ...newValues,
                };
                setValues(newOldValues);
              };

              const handleOpenAddContactForm = (): void => {
                setValues({ ...initialValues, deliveryDate: values.deliveryDate });
                setShowAddContactForm(true);
              };

              const hasValue = !!values.contact || !!values.location;
              const isSubmitDisabled = isScotMoves
                ? isAllAnimalsCanNotBeRemoved || !hasValue
                : isAllAnimalsCanNotBeRemoved;

              return (
                <>
                  <FieldDate
                    label="Date moved off-farm"
                    error={handleErrorCheck(errors.deliveryDate)}
                    inputProps={{
                      name: "deliveryDate",
                      value: values.deliveryDate,
                      onChange: (date): void => setFieldValue("deliveryDate", date),
                      disabled: isAllAnimalsCanNotBeRemoved,
                      maxDate: new Date(),
                    }}
                  />
                  <Flex container containerDirection="column" containerAlignItems="center" xs={12}>
                    <Spacer baselineHeight={1} border="top" />
                    <Flex item itemAlignSelf="center">
                      <h3>Where are you moving your animal(s) to?</h3>
                    </Flex>
                  </Flex>

                  <Spacer baselineHeight={1} />
                  {isShowMoveToBUSelector && (
                    <>
                      <BUWithLocationSelect
                        disabled={isDisableAllExceptForm}
                        // This functionality should only be available on a table with a single species, so we can
                        // assume that all animals in the set have the same species.
                        narrowBySpeciesId={animals[0].animalType?.id}
                        onLocationChange={handleLocationChange}
                        value={values.location}
                      />
                      <Flex container containerJustifyContent="center" xs="fill">
                        <Title tertiary>or</Title>
                      </Flex>
                    </>
                  )}
                  <AddressBookSelect
                    value={values.contact}
                    onContactChange={handleContactChange}
                    label="Move to Destination out of your Business"
                    disabled={isDisableAllExceptForm}
                  />
                  <Flex container containerJustifyContent="center" xs="fill">
                    <Title tertiary>or</Title>
                  </Flex>
                  <Spacer baselineHeight={1} />
                  <Button
                    caption="Enter manually"
                    onClick={handleOpenAddContactForm}
                    width="full"
                    disabled={isDisableAllExceptForm}
                  />
                  <Spacer baselineHeight={1} />
                  {showAddContactForm && (
                    <Flex container containerDirection="column" containerAlignItems="center" xs="fill">
                      <Flex item itemAlignSelf="center">
                        <Text smaller>
                          <b>
                            This address will be added to your address book. You must enter a {HoldingIDInitialism} or
                            address
                          </b>
                        </Text>
                      </Flex>
                      <Spacer baselineHeight={1} />
                    </Flex>
                  )}
                  {showAddContactForm ? (
                    <NewContactModalContent
                      onToggleShowForm={setShowAddContactForm}
                      onSubmitMarkOffFarm={handleFormSubmit}
                      deliveryDate={values.deliveryDate}
                      isLoading={requesting}
                    />
                  ) : (
                    <>
                      <FieldText
                        label="Notes (Optional)"
                        error={handleErrorCheck(errors.notes)}
                        inputProps={{
                          name: "notes",
                          placeholder: "Enter Notes",
                          disabled: isAllAnimalsCanNotBeRemoved,
                          value: values.notes,
                          onChange: handleChange,
                        }}
                      />
                      <Flex container containerJustifyContent="flex-end">
                        <Flex item>
                          <Button
                            caption="Cancel"
                            colour="grey"
                            variant="hollow"
                            onClick={handleCancelClick}
                            data-testid={TEST_IDS.MODAL_CANCEL_BUTTON_TEST_ID}
                          />
                        </Flex>
                        <Flex item itemGutter>
                          <Button
                            caption="Confirm"
                            disabled={requesting || isSubmitDisabled}
                            requesting={requesting}
                            onClick={handleSubmit}
                            data-testid={TEST_IDS.MODAL_CONFIRM_BUTTON_TEST_ID}
                          />
                        </Flex>
                      </Flex>
                    </>
                  )}
                </>
              );
            }}
          </Formik>
        </>
      </Modal>
    </>
  );
};
